import * as moment from "moment";

import { AnsweredQuestionWithId, DropdownOption, QuestionnaireData } from "../questionnaires/model";
import { ReportDetails } from "../reportsCommon/reportsCommon";

export const PHYSIOTHERAPY_REFERRAL_REPORT_KEY = "PHYSIOTHERAPY_REFERRAL_REPORT_KEY";

export interface PhysiotherapyReferralReportModel {
    questions: AnsweredQuestionWithId[];
    eTag: string | null | undefined;
    isDefaultCustomerType?: boolean;
    customerType?: string;
}

export enum ReportType {
    FullInitialAssessment = "FullInitialAssessment",
    ThirtyMinuteInitialAssessment = "ThirtyMinuteInitialAssessment",
    Update = "Update",
    DischargeAfterTreatment = "DischargeAfterTreatment"
}

export const reportTypeOptions: DropdownOption[] = [
    { text: "Full Initial Assessment", value: ReportType.FullInitialAssessment },
    { text: "30 minute Initial Assessment", value: ReportType.ThirtyMinuteInitialAssessment },
    { text: "Update", value: ReportType.Update },
    { text: "Discharge after Treatment", value: ReportType.DischargeAfterTreatment }
];

export enum WorkClassification {
    AtWorkMedicalIssue = "AtWorkMedicalIssue",
    AtWorkManagementIssue = "AtWorkManagementIssue",
    NotAtWorkMedicalIssue = "NotAtWorkMedicalIssue",
    NotAtWorkManagementIssue = "NotAtWorkManagementIssue"
}

export const workClassificationOptions: DropdownOption[] = [
    { text: "At Work - Medical issue", value: WorkClassification.AtWorkMedicalIssue },
    { text: "At Work - Management issue", value: WorkClassification.AtWorkManagementIssue },
    { text: "Not At Work - Medical issue", value: WorkClassification.NotAtWorkMedicalIssue },
    { text: "Not At Work - Management issue", value: WorkClassification.NotAtWorkManagementIssue }
];

export enum FitnessOutcome {
    FitForWork = "FitForWork",
    TemporarilyUnfitForWork = "TemporarilyUnfitForWork",
    PermanentlyUnfitForWork = "PermanentlyUnfitForWork"
}

export const fitnessOutcomeOptions: DropdownOption[] = [
    { text: "Fit for Work", value: FitnessOutcome.FitForWork },
    { text: "Temporarily Unfit for Work", value: FitnessOutcome.TemporarilyUnfitForWork },
    { text: "Permanently Unfit for Work", value: FitnessOutcome.PermanentlyUnfitForWork }
];

export const adjustmentOptions: DropdownOption[] = [
    { text: "No adjustments likely to be required", value: "NoAdjustments" },
    { text: "Short Term / Temporary Adjustments recommended", value: "ShortTerm" },
    { text: "Long Term Adjustments recommended", value: "LongTerm" }
];

export const adjustmentsRecommendedOptions = {
    ModifyInstructionsManuals: "Modify instructions and manuals",
    ModifyProcedures: "Modify procedures for testing, assessment or appraisal",
    ModifyWorkPatternsManagementSystems: "Modify work patterns or management systems",
    PotentiallyPermanentlyUnfitForDuties: "Potentially Permanently Unfit for Contracted Duties - OHP Review Pending",
    ProvideMentor: "Provide a mentor or ‘buddy’ while they regain confidence in the workplace",
    ProvideAlternativeWork: "Provide alternative work",
    ProvideNewModifiedEquipment: "Provide New or modified equipment or tools, including IT eqt",
    ProvideSupervision: "Provide supervision",
    ReallocateWorkWithinTeam: "Reallocate work within the team"
};

export const phasedReturnOptions: DropdownOption[] = [
    { text: "1 Week", value: "1Week" },
    { text: "2 Weeks", value: "2Weeks" },
    { text: "3 Weeks", value: "3Weeks" },
    { text: "4 Weeks", value: "4Weeks" },
    { text: "5 Weeks", value: "5Weeks" },
    { text: "6 Weeks +", value: "6Weeks+" },
];

export const managementDetailsOptions = {
    ManualTherapy: "Manual Therapy",
    AdviceSelfManagement: "Advice/Self-Management",
    ExerciseTherapy: "Exercise Therapy",
    ExerciseClass: "Exercise Class"
};

export const outcomeOptions: DropdownOption[] = [
    { text: "Fully Resolved", value: "FullyResolved" },
    { text: "Worse", value: "Worse" },
    { text: "Resolving", value: "Resolving" },
    { text: "DNA", value: "DNA" },
    { text: "Maintenance Achieved", value: "MaintenanceAchieved" },
    { text: "Onward Referral", value: "OnwardReferral" },
    { text: "No Change", value: "NoChange" }
];

export const workStatusAtInitialAssessmentOptions: DropdownOption[] = [
    { text: "Full Duties", value: "FullDuties" },
    { text: "Restricted Duties", value: "RestrictedDuties" },
    { text: "Off-Work", value: "Off-Work" }
];

export const workStatusAtDischargeOptions: DropdownOption[] = [
    { text: "Full Duties", value: "FullDuties" },
    { text: "Restricted Duties", value: "RestrictedDuties" },
    { text: "Off-Work", value: "Off-Work" }
];

export const PhysiotherapyReferralReportFormSections = {
    consultationDetailsSectionId: "ConsultationDetails",
    summarySectionId: "Summary",
    backgroundReasonsForReferralSectionId: "BackgroundReasonsForReferral",
    assessmentAndOpinionsSectionId: "AssessmentAndOpinions",
    recommendationsAndSummarySectionId: "RecommendationsAndSummary",
    physiotherapySpecificRecommendationsSectionId: "PhysiotherapySpecificRecommendations",
    dischargeAfterTreatmentInformation: "DischargeAfterTreatmentInformation",
    reportDetailsSectionId: "ReportDetails",
    reportToBeSharedWithManagerSectionId: "ReportToBeSharedWithManager",
    consentToReleaseReportSectionId: "ConsentToReleaseReport"
};

export interface CreatedSuccessorTask {
    readonly type: string;
    readonly description: string;
    readonly dueDate: moment.Moment;
}

export interface PhysiotherapyReferralReportDetails extends ReportDetails {
    id: string;
    data: QuestionnaireData;
    eTag: string;
    isDefaultCustomerType?: boolean;
    customerType?: string;
}
